import { cn } from "@/lib/cn";
import { fmt } from "@/lib/format";
import { medalLabel } from "@/lib/theme";
import type { AchievementPodiumRow } from "@/api/types";
import { MedalTray } from "@/components/cards/MedalTray";

export interface AchievementsRankingRowProps {
  row: AchievementPodiumRow;
  onClick?: () => void;
}

/** Logros-specific ranking row: rank pill + avatar + username +
 * total-points ticker + full MedalTray on the right. Glass panel, hover
 * lift. Podium rows stay owned by another component — this is purely the
 * list variant below the podium. */
export function AchievementsRankingRow({ row, onClick }: AchievementsRankingRowProps) {
  const initial = (row.username ?? "?").charAt(0).toUpperCase();
  const rankIsMedal = row.rank > 0 && row.rank <= 3;
  const earned = row.medals.filter((m) => m.unlocked).length;
  const total = row.medals.length;
  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      {...(onClick ? { type: "button" as const, onClick } : {})}
      className={cn(
        "group relative flex w-full items-center gap-4 rounded-[14px] border border-border-1 bg-white/[0.03] px-4 py-3 text-left backdrop-blur-sm",
        "transition-all duration-200",
        "hover:-translate-y-[1px] hover:border-border-2 hover:bg-white/[0.05] hover:shadow-md",
        "motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        onClick && "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-1",
      )}
    >
      <div
        className={cn(
          "flex h-8 w-10 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold tabular",
          rankIsMedal
            ? "bg-gradient-to-br from-brand-1 to-brand-3 text-white"
            : "bg-white/5 text-text-3",
        )}
        aria-label={`Posición ${row.rank}`}
      >
        {medalLabel(row.rank)}
      </div>

      <div
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-1/30 to-brand-3/30 text-sm font-semibold text-text-1 ring-1 ring-border-2"
        aria-hidden
      >
        {initial}
      </div>

      <div className="flex min-w-0 flex-shrink-0 flex-col" style={{ width: 180 }}>
        <div className="truncate font-semibold text-text-1">{row.username}</div>
        {row.team && (
          <div className="truncate text-xs text-text-3">{row.team}</div>
        )}
      </div>

      <div className="flex flex-shrink-0 flex-col items-end" style={{ width: 96 }}>
        <div className="tabular font-display text-lg font-bold text-text-1">
          {fmt(row.total_score)}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-text-4">
          puntos
        </div>
      </div>

      <div className="h-8 w-px flex-shrink-0 bg-border-1" aria-hidden />

      <div className="flex min-w-0 flex-1 items-center justify-end gap-3">
        <div
          className="flex flex-shrink-0 flex-col items-end leading-tight"
          aria-label={`${earned} de ${total} medallas conseguidas`}
        >
          <div className="tabular text-sm font-semibold text-text-2">
            <span className="text-text-1">{earned}</span>
            <span className="text-text-4">/{total}</span>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-text-4">
            medallas
          </div>
        </div>
        <MedalTray medals={row.medals} size="md" className="justify-end" />
      </div>
    </Wrapper>
  );
}
